import type { ButtonHTMLAttributes, ReactElement, Ref } from 'react';

import { DEFAULT_HUE, type Hue } from './hue';
import { Icon } from './Icon';
import styles from './IconButton.module.css';
import type { IconName } from './iconName';

/**
 * A square icon-only control — ADR-0101 §7.2, docs/DESIGN.md §7.3, §7.5.
 *
 * `label` is REQUIRED and becomes both `aria-label` and `title`. An icon carries no meaning on
 * its own in this system, so a nameless icon button is not a thing this component can express:
 * the type makes the accessible name impossible to forget rather than hoping a review catches
 * it.
 *
 * It is the Button, square: the same stylesheet (composed, not copied), the same hover layer,
 * ring and disabled treatment. `bare` is the default — the × of a banner, a toast, a dialog —
 * and `tinted` with a `hue` is for a control that speaks for a family, like the theme toggle
 * (peri). There is no `primary`: the one gradient control on a screen always has a word.
 *
 * Square at `--ctl` (44px on phones, 40 from 960) or `--ctl-sm` (44, 32 from 960).
 */

export type IconButtonVariant = 'bare' | 'tinted';
export type IconButtonSize = 'md' | 'sm';

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-label'
> {
  readonly icon: IconName;
  /** Already localised by the caller — `ui/**` does not translate (ADR-0003). */
  readonly label: string;
  /** Defaults to `'bare'`. */
  readonly variant?: IconButtonVariant;
  /** The family a `tinted` icon button speaks in. Defaults to `'peri'`; ignored when bare. */
  readonly hue?: Hue;
  readonly size?: IconButtonSize;
  readonly ref?: Ref<HTMLButtonElement>;
}

export function IconButton({
  icon,
  label,
  variant = 'bare',
  hue = DEFAULT_HUE,
  size = 'md',
  className,
  title,
  type = 'button',
  ...rest
}: IconButtonProps): ReactElement {
  const classes = [
    styles.base,
    variant === 'bare' ? styles.bare : undefined,
    size === 'sm' ? styles.sm : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      aria-label={label}
      title={title ?? label}
      data-hue={variant === 'tinted' ? hue : undefined}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}

export default IconButton;
